import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Switch,
  TextField
} from '@material-ui/core';
import {
  State,
  StateTransitionCondition,
  Workflow,
  WorkflowInvoke,
  WorkflowParams
} from '@sierralabs/workflow';

import { isSubTaskInActiveStatePath } from '../../../tasks/task.helper';
import { TaskContext } from '../../../tasks/Task.machine';
import { useFindWorkflow } from '../../Workflow.hooks';
import { WorkflowInvokeAction } from '../../Workflow.interface';
import {
  TransitionConditionConfiguratorProps,
  TransitionConditionDisplayProps,
  TransitionConditionPlugin
} from '../TransitionConditionPlugin';

export interface CheckGroupIdsOptions<T> {
  items: T[];
  groupIds: number[];
  // check: (item: T, groupId: number) => boolean;
  inGroup: (item: T, groupId: number) => boolean;
}

// items should match minimum number of group ids
export function checkGroupIds<T>(options: CheckGroupIdsOptions<T>) {
  const { items, groupIds, inGroup } = options;

  // allow more items per group but not less
  if (groupIds.length > 0 && groupIds.length > items.length) {
    return false;
  }

  // check only items that are part of group
  const groups: { id: number; index: number; items: T[] }[] = groupIds.map(
    id => {
      return { id, index: 0, items: items.filter(item => inGroup(item, id)) };
    }
  );
  // all groups should have at least 1 item
  if (groups.some(group => group.items.length === 0)) {
    return false;
  }

  // check all possible combinations to make sure
  // at least 1 unique user approves exactly from 1 group.
  // being unique per group means a user that is included in
  // multiple groups is never reused in the check
  let result: boolean | undefined;
  while (typeof result !== 'boolean') {
    // check with current index values
    // item may be undefined (e.g. no group.items)
    const values = groups
      .map((group): T | undefined => group.items[group.index])
      .filter((group): group is T => !!group);
    // if values are unique and match total groups, the final result is true
    if (new Set(values).size === groupIds.length) {
      result = true;
      break;
    }

    // increment index of items for the next check
    for (const [index, group] of Array.from(groups.entries())) {
      if (group.index < group.items.length - 1) {
        group.index++;
        // stop increment here and start check with current index values
        break;
      }
      // if this is the last group, check failed
      if (index >= groups.length - 1) {
        result = false;
        break;
      }
      // restart index for this group, then continue increment for next group
      group.index = 0;
    }
  }

  return result;
}

const Styled = {
  FormHelperText: styled(FormHelperText)`
    padding-left: 8px;
  `
};

const CheckSubTasksConfigurator: React.FC<
  TransitionConditionConfiguratorProps
> = ({ event, condition, onChange }) => {
  const invokes = Array.isArray(event.stateNode.invoke)
    ? event.stateNode.invoke
    : event.stateNode.invoke
    ? [event.stateNode.invoke]
    : [];
  const invoke = invokes?.find(
    (invoke: WorkflowInvoke) =>
      invoke.src === WorkflowInvokeAction.CreateSubTasks
  );

  // invoke?.data?.workflowId is deprecated
  const workflowId = invoke?.data?.workflowId || invoke?.data?.workflow?.id;
  // get subtask workflow if needed
  const [subTaskWorkflow, setSubTaskWorkflow] = useState<Workflow>();
  const { workflow: subTaskWorkflowDto, findWorkflow } =
    useFindWorkflow(workflowId);
  useEffect(() => workflowId && findWorkflow(), [findWorkflow, workflowId]);
  useEffect(() => {
    if (subTaskWorkflowDto && subTaskWorkflowDto.config) {
      setSubTaskWorkflow(new Workflow(subTaskWorkflowDto.config));
    }
  }, [subTaskWorkflowDto]);
  return (
    <>
      <Box mt={2}>
        <TextField
          select={Boolean(subTaskWorkflow)}
          disabled={!subTaskWorkflow}
          variant='outlined'
          fullWidth
          label={subTaskWorkflow ? 'Transition State' : 'Loading...'}
          helperText='Pick a state that all subtasks must reach before transitioning.'
          value={condition.params?.path || ''}
          onChange={event => {
            if (condition.params) {
              condition.params.path = event.target.value;
              onChange?.({ ...condition });
            }
          }}
        >
          {subTaskWorkflow?.getAllPaths().map(path => (
            <MenuItem key={path} value={path}>
              {path}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box mt={1}>
        <FormControl fullWidth>
          <FormControlLabel
            label='Partial'
            control={
              <Switch
                color='primary'
                checked={condition.params?.partial || false}
                onChange={(_, checked: boolean) => {
                  if (condition.params) {
                    condition.params.partial = checked;
                    onChange?.({ ...condition });
                  }
                }}
              />
            }
          />
          <Styled.FormHelperText>
            If partial is enabled, at least one (1) subtask must reach the
            selected state before transitioning.
          </Styled.FormHelperText>
        </FormControl>
      </Box>
    </>
  );
};

const CheckSubTasksDisplay: React.FC<TransitionConditionDisplayProps> = ({
  condition
}) => {
  return <>Check SubTasks</>;
};

export const CheckSubTasksPlugin: TransitionConditionPlugin = {
  type: 'check-subtasks',
  label: 'Check SubTasks',
  configurator: CheckSubTasksConfigurator,
  display: CheckSubTasksDisplay,
  canConfigure: event => {
    // only allow "Check SubTask" if create sub tasks action is invoked
    const invokes = Array.isArray(event.stateNode.invoke)
      ? event.stateNode.invoke
      : event.stateNode.invoke
      ? [event.stateNode.invoke]
      : [];
    const invoke = invokes?.find(
      (invoke: WorkflowInvoke) =>
        invoke.src === WorkflowInvokeAction.CreateSubTasks
    );
    const workflowId = invoke?.data?.workflowId || invoke?.data?.workflow?.id;
    return workflowId && invoke?.src === WorkflowInvokeAction.CreateSubTasks;
  },
  validate: (condition: Partial<StateTransitionCondition>) => {
    return true;
  },
  guard: (state: State<TaskContext>, params: WorkflowParams) => {
    console.log('guards: check-subtasks', state, params);
    const { task } = state.context;
    // make sure task.children has value
    // ignore check for optional subTasks
    // only check subTasks that are associated with the current task.status
    // unless their statePath is not set (default behavior)
    const subTasks = (task.children || []).filter(child => {
      return !child.isOptional && isSubTaskInActiveStatePath(child, task);
    });
    if (subTasks.length === 0) {
      console.warn('[check-subtasks] No task.children found.');
      return false;
    }
    const loop = params.partial ? 'some' : 'every';
    // check all regardless of user groups (required + ad-hoc approvers)
    const result = subTasks[loop](subTask => subTask.status === params.path);
    console.log('[check-subtasks] all subtasks checked', result);
    return result;
  }
};
